import SafeArea from "@/components/safe-area";
import StatusBarHeight from "@/components/status-bar-height";
import useInApp from "@/hooks/useIsApp";

import Button from "@/components/Button";
import { useMemo, useRef, useState } from "react";
import useEventListener from "@/hooks/useEventListener";
import clsx from "clsx";
import throttle from "lodash/throttle";
import { useEffect } from "react";
import { isIos } from "@/utils/parserDevice";
import NavigationBar from "../layout/NavigationBar";
import { BackUseIcon, ShareUseIcon } from "../layout/NavigationIcons";
import CoverContainer from "@/containers/layout/CoverContainer";

interface Props {
	children: React.ReactNode;
	onShare?: () => void;
	footerBtnText?: string;
	onFooterBtnClick?: () => void;
	bgUrl: string;
	hiddenFooterBtn?: boolean;
	title?: string;
	hiddenHeader?: boolean;
}
export default function OptionDocContainer(props: Props) {
	const isInApp = useInApp();
	const contentRef = useRef<HTMLDivElement>(null);
	const targetRef = useRef<HTMLDivElement>(null);
	const targetTop = useRef<number>(0);
	const [distance, setDistance] = useState(0);
	const navigationRef = useRef<HTMLDivElement>(null);
	const [mask, setMask] = useState(false);

	const [visible, setVisible] = useState(true);
	const timer = useRef<any>(null);
	const scrollTop = useRef(0);

	const getScrollTop = () => {
		let top = 0;
		if (contentRef.current) {
			top = contentRef.current.scrollTop;
		}
		return top;
	};

	const staticScroll = throttle(() => {
		const currentScrollTop = getScrollTop();
		const showVisible = scrollTop.current === 0 || scrollTop.current - currentScrollTop >= 0;
		// fixed: 滚到顶部 回弹效果
		if (currentScrollTop <= 0) {
			setVisible(true);
		} else {
			setVisible(showVisible);
		}

		scrollTop.current = currentScrollTop;

		if (timer.current) {
			clearTimeout(timer.current);
		}
		timer.current = setTimeout(() => {
			setVisible(true);
		}, 500);
	}, 300);

	useEventListener(
		"scroll",
		(e) => {
			if (targetRef.current) {
				const navigationHeight = navigationRef.current ? navigationRef.current.offsetHeight : 0;
				const rect = targetRef.current.getBoundingClientRect();

				const top = rect.top;
				if (top <= navigationHeight) {
					setMask(true);
				} else {
					setMask(false);
				}
				if (isIos()) {
					setDistance(rect.top - targetTop.current);
				}
			}

			staticScroll();
		},
		contentRef,
	);

	useEffect(() => {
		if (targetRef.current) {
			const rect = targetRef.current.getBoundingClientRect();
			targetTop.current = rect.top;
		}
	}, []);

	const imgScale = useMemo(() => {
		if (distance > 0) {
			const val = distance / targetTop.current;
			return 1 + val;
		}
		return 1;
	}, [distance]);
	const wrapperHeight = useMemo(() => {
		if (distance > 0) {
			return distance + "px";
		}
		return "0px";
	}, [distance]);

	return (
		<>
			<CoverContainer>
				<div className="navigation--wrapper relative mx-auto max-w-screen-sm overflow-y-hidden select-none">
					<div
						className="w-full h-0 pb-[100%] relative overflow-hidden"
						style={{
							boxSizing: "unset",
							height: wrapperHeight,
						}}
					>
						<div
							className="w-full h-full absolute left-0 top-0"
							style={{
								transform: `scale(${imgScale})`,
							}}
						>
							<img src={props.bgUrl} alt="bg" className="w-full h-full object-cover" />
						</div>
					</div>

					<div
						className={clsx(
							"absolute w-full left-0 top-0 z-10",
							mask ? "dark:bg-dark bg-white" : "dark:bg-transparent bg-transparent",
						)}
					>
						{!props.hiddenHeader && isInApp && (
							<div className={clsx("px-4", mask ? "text-dark dark:text-white" : "text-white")} ref={navigationRef}>
								<StatusBarHeight />
								<SafeArea position="top" />
								<NavigationBar
									leading={[<BackUseIcon key="back" backType="close" />]}
									title={props.title}
									trailing={[<ShareUseIcon key="share" onClick={props.onShare} />]}
								/>
							</div>
						)}
					</div>

					<div className="w-full h-full overflow-y-auto absolute left-0 top-0 hidden-scroll-bar z-0" ref={contentRef}>
						<div className="pt-[100%]">
							<div
								className="dark:bg-dark bg-white shadow-docOpenLight dark:shadow-docOpenDark relative -mt-5"
								style={{
									borderRadius: `${mask ? 0 : 1.25}rem ${mask ? 0 : 1.25}rem 0 0`,
								}}
								ref={targetRef}
							>
								<div className="w-full pb-7 pt-3.5 text-center text-[0px]">
									<div className="inline-block h-[5px] w-9 rounded bg-c3c3c3 dark:bg-black_60" />
								</div>

								<div className="px-4 pb-28 min-h-[100vh]">{props.children}</div>
							</div>
						</div>
					</div>

					{props.hiddenFooterBtn ? null : (
						<div
							className={clsx(
								"w-full fixed bottom-0 px-4 py-7 z-10 max-w-screen-sm left-1/2 mast__btn__bg",
								visible ? "footerShow" : "footerHidden",
							)}
						>
							<Button block onClick={props.onFooterBtnClick}>
								{props.footerBtnText}
							</Button>
						</div>
					)}
				</div>
				<style jsx>{`
					.footerShow {
						animation: fadeInUp 0.2s;
						animation-fill-mode: forwards;
					}
					.footerHidden {
						animation: fadeOutDown 0.2s;
						animation-fill-mode: forwards;
					}

					@keyframes fadeInUp {
						from {
							opacity: 0;
							transform: translate3d(-50%, 100%, 0);
						}
						to {
							opacity: 1;
							transform: translate3d(-50%, 0, 0);
						}
					}

					@keyframes fadeOutDown {
						from {
							opacity: 1;
							transform: translate3d(-50%, 0, 0);
						}
						to {
							opacity: 0;
							transform: translate3d(-50%, 100%, 0);
						}
					}
				`}</style>
			</CoverContainer>
		</>
	);
}
